import React, { useState, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import type {
  QualityTrendingAnalyticsProps,
  QualityTrendPoint,
  DateRange
} from '../../types/advanced_content_quality_ui';

// Chart colors
const CHART_COLORS = {
  quality: '#3B82F6',
  prediction: '#8B5CF6',
  benchmark: '#10B981',
  industry: '#F59E0B'
};

// Time range presets
const TIME_RANGES = [
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d' },
  { label: '90 Days', value: '90d' },
  { label: '6 Months', value: '6m' },
  { label: '1 Year', value: '1y' }
];

export const QualityTrendingAnalytics: React.FC<QualityTrendingAnalyticsProps> = ({
  trendingData,
  timeRange,
  comparisonMode = 'internal',
  showPredictions = false,
  onTrendClick
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'progression' | 'benchmarks' | 'cross-content' | 'predictions'>('overview');
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState<string>('overall');

  // Calculate trend summary statistics
  const trendSummary = useMemo(() => {
    const { qualityEvolution } = trendingData.historicalProgression;
    
    if (qualityEvolution.length < 2) {
      return { trend: 'stable', change: 0, current: 0, previous: 0 };
    }

    const current = qualityEvolution[qualityEvolution.length - 1]?.value || 0;
    const previous = qualityEvolution[qualityEvolution.length - 2]?.value || 0;
    const change = current - previous;
    
    let trend: 'improving' | 'declining' | 'stable' = 'stable';
    if (Math.abs(change) > 2) {
      trend = change > 0 ? 'improving' : 'declining';
    }

    return { trend, change, current, previous };
  }, [trendingData.historicalProgression.qualityEvolution]);

  // Get trend styling
  const getTrendStyle = (trend: string) => {
    switch (trend) {
      case 'improving':
        return { color: 'text-green-600', bg: 'bg-green-100', icon: '📈' };
      case 'declining':
        return { color: 'text-red-600', bg: 'bg-red-100', icon: '📉' };
      default:
        return { color: 'text-gray-600', bg: 'bg-gray-100', icon: '➡️' };
    }
  };

  // Render line chart
  const renderLineChart = (data: QualityTrendPoint[], color: string, height: number = 200) => {
    if (!data.length) return null;

    const maxValue = Math.max(...data.map(d => d.value));
    const minValue = Math.min(...data.map(d => d.value));
    const range = maxValue - minValue || 1;
    const padding = range * 0.1;

    const points = data.map((point, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = 100 - ((point.value - minValue + padding) / (range + 2 * padding)) * 100;
      return { x, y, point };
    });

    const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return (
      <div className="relative" style={{ height }}>
        <svg width="100%" height="100%" className="overflow-visible">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line
              key={y}
              x1="0%"
              y1={`${y}%`}
              x2="100%"
              y2={`${y}%`}
              stroke="#E5E7EB"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          ))}
          
          {/* Trend line */}
          <path
            d={pathData}
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Data points */}
          {points.map((p, index) => (
            <circle
              key={index}
              cx={`${p.x}%`}
              cy={`${p.y}%`}
              r="4"
              fill={color}
              className="cursor-pointer hover:r-6 transition-all"
              onClick={() => onTrendClick?.(p.point)}
            />
          ))}
          
          {/* Area fill */}
          <path
            d={`${pathData} L 100 100 L 0 100 Z`}
            fill={color}
            fillOpacity="0.1"
          />
        </svg>
        
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500 -ml-8">
          <span>{maxValue.toFixed(0)}%</span>
          <span>{((maxValue + minValue) / 2).toFixed(0)}%</span>
          <span>{minValue.toFixed(0)}%</span>
        </div>
      </div>
    );
  };

  // Render overview tab
  const renderOverviewTab = () => {
    const trendStyle = getTrendStyle(trendSummary.trend);
    
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">📊</span>
              <div>
                <div className="text-sm text-gray-600">Current Score</div>
                <div className="text-xl font-semibold text-gray-900">
                  {trendSummary.current.toFixed(1)}%
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{trendStyle.icon}</span>
              <div>
                <div className="text-sm text-gray-600">Trend</div>
                <div className={`text-xl font-semibold ${trendStyle.color}`}>
                  {trendSummary.change > 0 ? '+' : ''}{trendSummary.change.toFixed(1)}%
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">⚡</span>
              <div>
                <div className="text-sm text-gray-600">Improvement Rate</div>
                <div className="text-xl font-semibold text-gray-900">
                  {trendingData.historicalProgression.improvementRate.toFixed(1)}%
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">📈</span>
              <div>
                <div className="text-sm text-gray-600">Volatility</div>
                <div className="text-xl font-semibold text-gray-900">
                  {trendingData.historicalProgression.volatility.toFixed(1)}%
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Trend Chart */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Quality Evolution</h3>
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
            >
              {TIME_RANGES.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
          
          {renderLineChart(
            trendingData.historicalProgression.qualityEvolution,
            CHART_COLORS.quality,
            300
          )}
        </Card>

        {/* Milestones */}
        {trendingData.historicalProgression.milestones.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Milestones</h3>
            <div className="space-y-3">
              {trendingData.historicalProgression.milestones.map((milestone, index) => (
                <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-3 h-3 rounded-full ${
                    milestone.significance === 'major' ? 'bg-blue-500' : 'bg-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{milestone.achievement}</div>
                    <div className="text-sm text-gray-600">
                      {milestone.date.toLocaleDateString()} • Score: {milestone.qualityScore}%
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    milestone.significance === 'major' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {milestone.significance}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // Render benchmarks tab
  const renderBenchmarksTab = () => {
    const { industryStandards, internalBenchmarks, competitorComparisons } = trendingData.benchmarkingComparisons;
    
    return (
      <div className="space-y-6">
        {/* Comparison Mode Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
          {(['internal', 'industry', 'both'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => {/* Update comparison mode */}}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                comparisonMode === mode
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>

        {/* Industry Standards */}
        {(comparisonMode === 'industry' || comparisonMode === 'both') && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Industry Standards</h3>
            <div className="space-y-4">
              {industryStandards.map((standard, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{standard.category}</div>
                    <div className="text-sm text-gray-600">
                      Current: {standard.currentValue}% | Benchmark: {standard.benchmarkValue}%
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      standard.status === 'above' ? 'bg-green-100 text-green-800' :
                      standard.status === 'meeting' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {standard.status}
                    </span>
                    <span className={`text-sm font-medium ${
                      standard.variance >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {standard.variance > 0 ? '+' : ''}{standard.variance.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Internal Benchmarks */}
        {(comparisonMode === 'internal' || comparisonMode === 'both') && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Internal Benchmarks</h3>
            <div className="space-y-4">
              {internalBenchmarks.map((benchmark, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{benchmark.category}</div>
                    <div className="text-sm text-gray-600">
                      Current: {benchmark.currentValue}% | Target: {benchmark.benchmarkValue}%
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      benchmark.status === 'above' ? 'bg-green-100 text-green-800' :
                      benchmark.status === 'meeting' ? 'bg-blue-100 text-blue-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {benchmark.status}
                    </span>
                    <span className={`text-sm font-medium ${
                      benchmark.variance >= 0 ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {benchmark.variance > 0 ? '+' : ''}{benchmark.variance.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // Render cross-content analysis tab
  const renderCrossContentTab = () => {
    const { contentTypeAnalysis, crossTypeComparisons, qualityPatterns } = trendingData.crossContentAnalysis;
    
    return (
      <div className="space-y-6">
        {/* Content Type Analysis */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality by Content Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contentTypeAnalysis.map((analysis, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 capitalize">{analysis.contentType}</h4>
                  <span className="text-lg font-bold text-blue-600">
                    {analysis.averageQuality.toFixed(1)}%
                  </span>
                </div>
                
                {analysis.commonIssues.length > 0 && (
                  <div className="mb-3">
                    <div className="text-sm text-gray-600 mb-1">Common Issues:</div>
                    <div className="flex flex-wrap gap-1">
                      {analysis.commonIssues.slice(0, 3).map((issue, issueIndex) => (
                        <span key={issueIndex} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                          {issue}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.bestPerformers.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Top Performers:</div>
                    <div className="text-xs text-green-700 space-y-1">
                      {analysis.bestPerformers.slice(0, 2).map((performer, perfIndex) => (
                        <div key={perfIndex}>{performer}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Quality Patterns */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Patterns</h3>
          <div className="space-y-4">
            {qualityPatterns.map((pattern, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{pattern.pattern}</h4>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">
                      Frequency: {pattern.frequency}
                    </span>
                    <span className={`text-sm font-medium ${
                      pattern.qualityCorrelation > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      Correlation: {pattern.qualityCorrelation > 0 ? '+' : ''}{(pattern.qualityCorrelation * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-700">
                  {pattern.actionableInsights.join(' • ')}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  // Render predictions tab
  const renderPredictionsTab = () => {
    if (!showPredictions) {
      return (
        <Card className="p-8 text-center">
          <span className="text-4xl mb-4 block">🔮</span>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Predictions Disabled</h3>
          <p className="text-gray-600">Enable predictions to view quality forecasts and analytics.</p>
        </Card>
      );
    }

    const { qualityForecast, riskAssessment, improvementOpportunities } = trendingData.predictiveAnalytics;
    
    return (
      <div className="space-y-6">
        {/* Quality Forecast Chart */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Forecast</h3>
          {renderLineChart(
            qualityForecast.map(f => ({ timestamp: f.date, value: f.predictedScore })),
            CHART_COLORS.prediction,
            250
          )}
        </Card>

        {/* Risk Assessment */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h3>
          <div className="space-y-3">
            {riskAssessment.map((risk, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{risk.risk}</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      Probability: {(risk.probability * 100).toFixed(0)}%
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      risk.impact > 0.7 ? 'bg-red-100 text-red-800' :
                      risk.impact > 0.4 ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {risk.impact > 0.7 ? 'High' : risk.impact > 0.4 ? 'Medium' : 'Low'} Impact
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-700 mb-2">{risk.mitigation}</div>
                <div className="text-xs text-gray-600">Timeline: {risk.timeline}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Improvement Opportunities */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Improvement Opportunities</h3>
          <div className="space-y-3">
            {improvementOpportunities.map((opportunity, index) => (
              <div key={index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-green-900">{opportunity.opportunity}</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-green-700">
                      Gain: +{opportunity.potentialGain.toFixed(1)}%
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      opportunity.effort === 'low' ? 'bg-green-100 text-green-800' :
                      opportunity.effort === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {opportunity.effort} effort
                    </span>
                  </div>
                </div>
                <div className="text-sm text-green-700 mb-2">Timeline: {opportunity.timeline}</div>
                {opportunity.prerequisites.length > 0 && (
                  <div className="text-xs text-green-600">
                    Prerequisites: {opportunity.prerequisites.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'progression', label: 'Progression', icon: '📈' },
    { id: 'benchmarks', label: 'Benchmarks', icon: '🎯' },
    { id: 'cross-content', label: 'Cross-Content', icon: '🔄' },
    ...(showPredictions ? [{ id: 'predictions', label: 'Predictions', icon: '🔮' }] : [])
  ] as const;

  return (
    <div className="quality-trending-analytics space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Quality Trending Analytics</h2>
          <p className="text-gray-600 mt-1">Historical analysis and quality insights</p>
        </div>
        
        <div className={`px-4 py-2 rounded-lg ${getTrendStyle(trendSummary.trend).bg}`}>
          <div className="flex items-center space-x-2">
            <span>{getTrendStyle(trendSummary.trend).icon}</span>
            <span className={`font-medium ${getTrendStyle(trendSummary.trend).color}`}>
              {trendSummary.trend.charAt(0).toUpperCase() + trendSummary.trend.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'progression' && renderOverviewTab()}
        {activeTab === 'benchmarks' && renderBenchmarksTab()}
        {activeTab === 'cross-content' && renderCrossContentTab()}
        {activeTab === 'predictions' && renderPredictionsTab()}
      </div>
    </div>
  );
};

export default QualityTrendingAnalytics;
